import React, { useState } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useOcrAccountsAggregate } from "@/features/accounts/ocr/hooks/use-ocraccounts-aggregate";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { FileUploader } from "@/shared/components/file-uploader/upload-file";
import BaseDialog from "@/shared/components/dialog/base-dialog";
import { OcrProcessStatus } from "@/shared/config/globals";

interface Props {
  onTapCloseModal: () => void;
  onTapCompleteModal?: () => void;
  ocrAccountId?: string;
}

// ファイル取込モーダル
export const FileAggregateModal = (props: Props) => {
  const {
    onTapCloseModal,
    onTapCompleteModal,
    ocrAccountId,
  } = props;

  const {
    selectedAccountGroup,
    selectedAccount,
    setSelectedAccount,
    setAccounts,
  } = useAccountGroupsContext();

  const {
    uploadFile,
    ocrAccountsAggregate,
  } = useOcrAccountsAggregate();

  // 明細取込用ファイルでファイルの保持処理を実装
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [invalidFiles, setInvalidFiles] = useState<string[]>([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // アップロード開始ボタン押下時処理
  const handleAggregate = async () => {
    const accountId = ocrAccountId || selectedAccount?.mlinkAccountId;
    if (selectedFiles.length > 0 && accountId) {
      // ファイルアップロード
      const uploadRes = await uploadFile(selectedAccountGroup.accountGroupId, accountId, selectedFiles);
      console.log("UploadRes:", uploadRes);
      if (!uploadRes) {
        return;
      }
      // 取込できないファイルがあったら警告表示
      if (uploadRes.invalid_files && uploadRes.invalid_files.length > 0) {
        setInvalidFiles(uploadRes.invalid_files);
        setShowErrorDialog(true);
        return;
      }

      // アップロード準備ができたらOCR明細取込開始
      const aggRes = await ocrAccountsAggregate(
        selectedAccountGroup.accountGroupId, 
        accountId, 
        uploadRes.ocr_process_id
      );
      if (!aggRes) {
        return;
      }
      
      // 処理した口座と選択状態の口座が一致する場合のみ，口座情報へprocessIdをセットする
      if (selectedAccount && selectedAccount.mlinkAccountId === accountId) {
        setSelectedAccount({
          ...selectedAccount,
          ocrProcessId: uploadRes.ocr_process_id,
          ocrProcessStatus: OcrProcessStatus.Processing,
        });
      }

      setAccounts(prev => prev.map(acc =>
        acc.mlinkAccountId === accountId && acc.passbookType === "101"
          ? { ...acc, ocrProcessId: uploadRes.ocr_process_id, ocrProcessStatus: OcrProcessStatus.Processing }
          : acc
      ));
    }
    // モーダルを閉じる
    if (onTapCompleteModal) {
      onTapCompleteModal();
    }
    else {
      onTapCloseModal();
    }
  };

  // 取込不可ファイル警告ダイアログを閉じるときの処理
  const handleCloseErrorDialog = () => {
    const newFiles = selectedFiles.filter(f => !invalidFiles.includes(f.name));
    setSelectedFiles(newFiles);
    setInvalidFiles([]);
    setShowErrorDialog(false);
  };

  // OCRファイルアップロードボタンに付与するID
  const ocrFileUploadButton = "ocr_file_upload_button";

  return (
    <>
      <BasePageModal
        title="ファイルを選択して取り込み"
        className="md-add-ocr"
        handleClose={() => {
          if (onTapCompleteModal) {
            onTapCompleteModal();
          } else {
            onTapCloseModal();
          }
        }}
        bottomChildren={
          <BaseRoundButton
            buttonText="アップロード"
            className="clos-modal"
            onClick={handleAggregate}
            isActive={selectedFiles.length > 0 ? true : false}
            id={ocrFileUploadButton}
            loadingNegative={true}
          />
        }
        disableOverlayClose={true}
      >
        <h2>通帳の画像を選択してください</h2>
        <ul>
          <li>
            <FileUploader
              accept="image/png, image/jpg, image/jpeg, application/pdf, image/heic"
              multiple={true}
              onFilesSelected={(files) => setSelectedFiles(files)}
              maxFiles={5}
              maxTotalSizeMB={10}
              files={selectedFiles}
            />
          </li>
        </ul>

        {/* 警告ダイアログ */}
        <BaseDialog
          isShow={showErrorDialog}
          title={<h2>ファイルのアップロードに失敗しました</h2>}
          rightButton={
            <BaseRoundButton
              className="select-dialog__button"
              buttonText="閉じる"
              onClick={handleCloseErrorDialog}
            />
          }
        >
          <div style={{ textAlign: "center", padding: "1rem" }}>
            <p style={{ marginBottom: "1rem" }}>以下のファイルの取込ができませんでした。最初からお試しください。</p>
            <p style={{ marginBottom: "1rem" }}>以下のファイルは一度取り込んだことがある、あるいはファイルにパスワードがかかっている可能性があります。</p>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {invalidFiles.map((fileName, index) => (
                <li key={index} style={{ color: "#d32f2f", fontWeight: "bold" }}>{fileName}</li>
              ))}
            </ul>
          </div>
        </BaseDialog>
      </BasePageModal>
    </>
  );
};